import { Color } from "@repo/app-types/constants/colors";
import {
  automationDeliveryProviderSchema,
  type AutomationDeliveryProvider as AutomationDeliveryProviderType,
} from "@repo/app-types/entities/automation";
import type { AutomationArtifactSummary } from "@repo/app-types/entities/automationArtifact";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { FC } from "react";
import { useMemo } from "react";
import { Size } from "../helpers/helpers.js";
import { Pill } from "../Pills/pill.js";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "../tables/entityTable.js";
import {
  automationDeliveryProviderLabels,
  AutomationRunPreviewPill,
  formatRunTime,
} from "./automationPresentation.js";

/**
 * Describes an artifact's audience for display: the `to` recipient name(s) and
 * the channels it's delivered to, then each subscriber as "cc: <name>". Falls
 * back to a generic label when a teammate or channel name can't be resolved,
 * and to "—" when there is no audience at all.
 */
export function describeArtifactRecipients(
  artifact: AutomationArtifactSummary,
  teammateNamesById: ReadonlyMap<TeammateId, string>,
): string {
  const toNames = artifact.recipients
    .filter((recipient) => recipient.type === "to")
    .map(
      (recipient) =>
        teammateNamesById.get(recipient.teammate_id) ?? "A teammate",
    );
  // Channel names are stored as configured, already carrying the provider's
  // prefix (e.g. "# reports").
  const channelNames = artifact.channels.map(
    (channel) => channel.channel_name ?? "A channel",
  );
  const ccNames = artifact.recipients
    .filter((recipient) => recipient.type === "cc")
    .map(
      (recipient) =>
        `cc: ${teammateNamesById.get(recipient.teammate_id) ?? "a teammate"}`,
    );

  const names = [...toNames, ...channelNames, ...ccNames];
  return names.length === 0 ? "—" : names.join(", ");
}

/**
 * How the report actually reached its audience — "Email", "Slack", or both when
 * a Slack agent fell back to email for a recipient with no Slack user. Read from
 * what was delivered rather than the agent's configured provider, so it stays
 * truthful for a mixed send, and "—" until something has gone out.
 */
export function describeArtifactDeliveryProviders(
  artifact: AutomationArtifactSummary,
): string {
  const providers = new Set<AutomationDeliveryProviderType>();
  for (const recipient of artifact.recipients) {
    // A recipient's provider is stamped with their `sent_at`, so a set one
    // already means delivered.
    if (recipient.provider) {
      providers.add(recipient.provider);
    }
  }
  for (const channel of artifact.channels) {
    // A channel's provider is stored when the report is saved, so it says
    // nothing about delivery on its own — `sent_at` does.
    if (channel.sent_at) {
      providers.add(channel.provider);
    }
  }
  if (providers.size === 0) {
    return "—";
  }
  // Ordered by the provider schema so a mixed send reads the same every time.
  return automationDeliveryProviderSchema.options
    .filter((provider) => providers.has(provider))
    .map((provider) => automationDeliveryProviderLabels[provider])
    .join(", ");
}

/**
 * The time an artifact was sent: the latest `sent_at` across its audience. The
 * To teammate and CC'd subscribers are reached by one email, and a channel is
 * stamped when the report is posted to it. `null` until at least one of them has
 * been reached (the report is saved but hasn't gone out yet).
 */
export function resolveArtifactSentAt(
  artifact: Pick<AutomationArtifactSummary, "recipients" | "channels">,
): string | null {
  let latest: string | null = null;
  for (const audience of [...artifact.recipients, ...artifact.channels]) {
    if (
      audience.sent_at &&
      (latest === null || new Date(audience.sent_at) > new Date(latest))
    ) {
      latest = audience.sent_at;
    }
  }
  return latest;
}

export interface AutomationArtifactsTableProps {
  artifacts: readonly AutomationArtifactSummary[] | undefined;
  teammateNamesById: ReadonlyMap<TeammateId, string>;
  isLoading: boolean;
  /** Called when the user activates a row, e.g. to open the artifact. */
  onArtifactAction: (artifact: AutomationArtifactSummary) => void;
}

export const AutomationArtifactsTable: FC<AutomationArtifactsTableProps> = (
  props,
) => {
  const { artifacts, teammateNamesById, isLoading, onArtifactAction } = props;
  const columns = useMemo<
    readonly EntityTableColumnDefinition<AutomationArtifactSummary>[]
  >(
    () => [
      {
        id: "title",
        header: "Result",
        isRowHeader: true,
        getTextValue: (artifact) => artifact.title,
        renderCell: (artifact) => (
          <span className="flex items-center gap-2">
            <span className="min-w-0 font-medium text-foreground">
              {artifact.title}
            </span>
            {artifact.is_run_preview && <AutomationRunPreviewPill />}
            {artifact.is_empty && (
              // Distinguishes empty reports when the view is set to show them
              // (they're hidden by default).
              <Pill
                label="No findings"
                color={Color.Gray}
                size={Size.SMALL}
                className="shrink-0"
              />
            )}
          </span>
        ),
      },
      {
        id: "recipients",
        header: "Recipients",
        getTextValue: (artifact) =>
          describeArtifactRecipients(artifact, teammateNamesById),
        renderCell: (artifact) => (
          <span className="text-muted-foreground">
            {describeArtifactRecipients(artifact, teammateNamesById)}
          </span>
        ),
      },
      {
        id: "deliveryChannel",
        header: "Delivery channel",
        getTextValue: (artifact) => describeArtifactDeliveryProviders(artifact),
        renderCell: (artifact) => (
          <span className="text-muted-foreground">
            {describeArtifactDeliveryProviders(artifact)}
          </span>
        ),
      },
      {
        id: "sent",
        header: "Sent",
        getTextValue: (artifact) => {
          const sentAt = resolveArtifactSentAt(artifact);
          return sentAt ? formatRunTime(sentAt) : "—";
        },
        renderCell: (artifact) => {
          const sentAt = resolveArtifactSentAt(artifact);
          return (
            <span className="text-muted-foreground">
              {sentAt ? formatRunTime(sentAt) : "—"}
            </span>
          );
        },
      },
    ],
    [teammateNamesById],
  );

  return (
    <EntityTable
      aria-label="Agent results"
      columns={columns}
      entities={artifacts}
      isLoading={isLoading}
      emptyMessage="No results yet. Results appear here once this agent runs."
      onEntityAction={onArtifactAction}
      size={Size.SMALL}
    />
  );
};
